import { useQuery } from '@tanstack/react-query';
import { Check, Paperclip, X } from 'lucide-react';
import { useEffect, useEffectEvent, useId, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { LIMITS } from '@sbg/shared/constants';
import type { QuantityUnit } from '@sbg/shared/constants';
import { AppLink } from '@/components/ui/AppLink';
import { Button } from '@/components/ui/Button';
import { buttonClasses } from '@/components/ui/button-classes';
import { Checkbox } from '@/components/ui/Checkbox';
import { FieldError } from '@/components/ui/FieldError';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { PhoneField } from '@/components/ui/PhoneField';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { track } from '@/lib/analytics';
import { apiFetch, isApiError, newIdempotencyKey } from '@/lib/api/client';
import { applyFormErrors } from '@/lib/api/form-errors';
import { useAuth } from '@/lib/auth';
import { fetchProduct } from '@/lib/catalog';
import { lazyResolver } from '@/lib/forms/lazy-resolver';
import { interpolate } from '@/lib/i18n';
import { queryKeys } from '@/lib/api/keys';
import { Reveal } from '@/motion/Reveal';
import { content } from './content';
import { checkAttachment } from './attachment';
import type { AttachmentProblem } from './attachment';
import type { EnquiryFormInput, EnquiryFormValues } from './form-schema';
import type { EnquiryPrefill } from './prefill';
import { SelectedStones } from './SelectedStones';
import { Turnstile } from './Turnstile';

// Zod and the schema are fetched when the form is first touched, not with the page (lib/forms/lazy-resolver.ts)
const resolver = lazyResolver<EnquiryFormInput, unknown, EnquiryFormValues>(() =>
  import('./enquiry-resolver').then((module) => module.enquiryResolver),
);

/** A number box that was left empty is "not given" (the schema's field is optional), not 0 or NaN. */
const toOptionalNumber = (value: unknown): number | undefined =>
  value === '' || value === null || value === undefined ? undefined : Number(value);

interface Created {
  ref: string;
  status: string;
  attachmentSaved: boolean;
}

export interface EnquiryFormProps {
  prefill: EnquiryPrefill;
}

/**
 * The enquiry form (`/contact-us#enquiry`): validated with the API's own schema, pre-filled from the
 * address (the stone it was started from, its variant, the topic) and from the signed-in customer, with an optional
 * attachment, a honeypot, an optional Turnstile challenge and an idempotent submit (a double click
 * or a retry never creates two enquiries). Success replaces the form with the reference number.
 */
export function EnquiryForm({ prefill }: EnquiryFormProps) {
  const copy = content;
  const { user, isAuthenticated } = useAuth();
  const ids = { form: useId(), file: useId() };

  const [slugs, setSlugs] = useState<string[]>(prefill.products);
  // the unit goes with the quantity: it is sent only when a quantity was typed (so it is not a form field)
  const [unit, setUnit] = useState<QuantityUnit>('SQ_FT');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [attachmentProblem, setAttachmentProblem] = useState<AttachmentProblem>(null);
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState<{ ref: string; email: string } | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
  const successRef = useRef<HTMLHeadingElement | null>(null);

  const form = useForm<EnquiryFormInput, unknown, EnquiryFormValues>({
    resolver,
    mode: 'onTouched',
    defaultValues: {
      name: user?.name ?? '',
      phone: user?.phone ?? '',
      email: user?.email ?? '',
      city: '',
      message: prefill.topic ? copy.topics[prefill.topic] : '',
      consent: false,
    },
  });
  const {
    control,
    register,
    handleSubmit,
    reset,
    getValues,
    setError,
    formState: { errors, isSubmitting, isDirty },
  } = form;

  // The session may arrive a moment after the form: fill in the customer's details if nothing was typed yet.
  const fillFrom = useEffectEvent((customer: NonNullable<typeof user>) => {
    if (isDirty) return;
    reset({
      ...getValues(),
      name: customer.name,
      email: customer.email,
      phone: customer.phone ?? '',
    });
  });
  useEffect(() => {
    if (user) fillFrom(user);
  }, [user]);

  useEffect(() => {
    if (done) successRef.current?.focus();
  }, [done]);

  // A variant belongs to the first stone only, and only if that stone really has it.
  const firstSlug = slugs[0];
  const first = useQuery({
    queryKey: queryKeys.catalog.product(firstSlug ?? ''),
    queryFn: ({ signal }) => fetchProduct(firstSlug ?? '', signal),
    enabled: Boolean(firstSlug && prefill.variantId),
    staleTime: 5 * 60_000,
  });
  const variantId =
    prefill.variantId && first.data?.variants.some((variant) => variant.id === prefill.variantId)
      ? prefill.variantId
      : undefined;

  const onFile = (file: File | null): void => {
    if (!file) {
      setAttachment(null);
      setAttachmentProblem(null);
      return;
    }
    const problem = checkAttachment(file);
    setAttachmentProblem(problem);
    setAttachment(problem ? null : file);
  };

  const send = async (values: EnquiryFormValues, website: string): Promise<void> => {
    setFormError(null);
    const body = {
      name: values.name,
      phone: values.phone,
      email: values.email,
      ...(values.city ? { city: values.city } : {}),
      ...(values.thicknessMm !== undefined ? { thicknessMm: values.thicknessMm } : {}),
      ...(values.quantity !== undefined ? { quantity: values.quantity, quantityUnit: unit } : {}),
      ...(values.size ? { size: values.size } : {}),
      message: values.message,
      consent: true,
      source: slugs.length > 0 ? 'PRODUCT_PAGE' : 'CONTACT_FORM',
      products: slugs.map((slug, index) => ({
        slug,
        ...(index === 0 && variantId ? { variantId } : {}),
      })),
      website,
      ...(captcha ? { captchaToken: captcha } : {}),
    };
    try {
      let payload: FormData | typeof body = body;
      if (attachment) {
        const data = new FormData();
        data.set('data', JSON.stringify(body));
        data.set('attachment', attachment);
        payload = data;
      }
      const created = await apiFetch<Created>('/enquiries', {
        method: 'POST',
        body: payload,
        idempotencyKey,
      });
      setIdempotencyKey(newIdempotencyKey());
      setDone({ ref: created.ref, email: values.email });
      // analytics (consent-gated, closed list): whether a requirement was given and how many stones — never its content
      track('enquiry_submitted', {
        requirement:
          values.thicknessMm !== undefined || values.quantity !== undefined || Boolean(values.size),
        products: slugs.length,
        attachment: attachment !== null,
      });
    } catch (error) {
      if (isApiError(error) && error.status === 429) setFormError(copy.errors.rateLimited);
      else if (isApiError(error) && error.code === 'NETWORK_ERROR')
        setFormError(copy.errors.network);
      else {
        const mapped = applyFormErrors(setError, error);
        if (mapped.form === null && Object.keys(mapped.fields).length === 0) {
          setFormError(copy.errors.generic);
        } else if (mapped.form !== null) {
          setFormError(mapped.form);
        }
      }
    }
  };

  if (done) {
    return (
      <div className="enquiry-done" role="status">
        <Reveal>
          <span className="enquiry-done__mark" aria-hidden="true">
            <Check size={28} strokeWidth={2.5} />
          </span>
        </Reveal>
        <Reveal delay={100}>
          <h2 id="enquiry-heading" ref={successRef} tabIndex={-1} className="t-h3">
            {copy.success.title}
          </h2>
        </Reveal>
        <Reveal delay={180} className="enquiry-done__ref">
          <span className="t-small text-fg-muted">{copy.success.reference}</span>
          <strong className="enquiry-done__code">{done.ref}</strong>
        </Reveal>
        <Reveal delay={240}>
          <p className="t-body text-fg-muted">{copy.success.body}</p>
        </Reveal>
        <Reveal delay={300} className="enquiry-done__actions">
          <AppLink to="/shop" className={buttonClasses({ variant: 'primary' })}>
            {copy.success.browse}
          </AppLink>
          <Button
            variant="glass"
            onClick={() => {
              setDone(null);
              reset({ ...getValues(), message: '', consent: false });
              setSlugs([]);
              setUnit('SQ_FT');
              setAttachment(null);
            }}
          >
            {copy.success.again}
          </Button>
        </Reveal>
      </div>
    );
  }

  const f = copy.form;
  return (
    <form
      className="enquiry"
      noValidate
      onFocusCapture={resolver.preload}
      onSubmit={(event) => {
        // the honeypot is read from the form itself, so bots that set the value without events are caught too
        const raw = new FormData(event.currentTarget).get('website');
        const website = typeof raw === 'string' ? raw : '';
        void handleSubmit((values) => send(values, website))(event);
      }}
      aria-describedby={formError ? `${ids.form}-error` : undefined}
    >
      <div className="enquiry__intro">
        <h2 id="enquiry-heading" tabIndex={-1} className="t-h3">
          {f.title}
        </h2>
        <p className="t-body text-fg-muted">{f.lead}</p>
      </div>

      {isAuthenticated && user ? (
        <p className="enquiry__signed-in t-small text-fg-muted">
          {interpolate(f.signedInAs, { name: user.name })}
        </p>
      ) : null}

      <div className="enquiry__grid">
        <div>
          <Label htmlFor={`${ids.form}-name`} required>
            {f.name}
          </Label>
          <Input
            id={`${ids.form}-name`}
            autoComplete="name"
            placeholder={f.namePlaceholder}
            invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? `${ids.form}-name-error` : undefined}
            {...register('name')}
          />
          <FieldError id={`${ids.form}-name-error`} message={errors.name?.message} />
        </div>

        <div>
          <Label htmlFor={`${ids.form}-phone`} required>
            {f.phone}
          </Label>
          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <PhoneField
                id={`${ids.form}-phone`}
                name={field.name}
                inputRef={field.ref}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder={f.phonePlaceholder}
                labels={{ country: f.countryCode, search: f.countrySearch, none: f.countryNone }}
                invalid={Boolean(errors.phone)}
                aria-describedby={errors.phone ? `${ids.form}-phone-error` : undefined}
              />
            )}
          />
          <FieldError id={`${ids.form}-phone-error`} message={errors.phone?.message} />
        </div>

        <div>
          <Label htmlFor={`${ids.form}-email`} required>
            {f.email}
          </Label>
          <Input
            id={`${ids.form}-email`}
            type="email"
            autoComplete="email"
            placeholder="name@example.com"
            invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? `${ids.form}-email-error` : undefined}
            {...register('email')}
          />
          <FieldError id={`${ids.form}-email-error`} message={errors.email?.message} />
        </div>

        <div>
          <Label htmlFor={`${ids.form}-city`}>
            {f.city} <span className="enquiry__optional">({f.optional})</span>
          </Label>
          <Input
            id={`${ids.form}-city`}
            autoComplete="address-level2"
            maxLength={LIMITS.city.max}
            invalid={Boolean(errors.city)}
            aria-describedby={errors.city ? `${ids.form}-city-error` : undefined}
            {...register('city')}
          />
          <FieldError id={`${ids.form}-city-error`} message={errors.city?.message} />
        </div>

        <p className="enquiry__wide enquiry__needs" id={`${ids.form}-needs`}>
          {f.needs} <span className="enquiry__optional">({f.optional})</span>
        </p>

        <div
          className="enquiry__wide enquiry__trio"
          role="group"
          aria-labelledby={`${ids.form}-needs`}
        >
          <div>
            <Label htmlFor={`${ids.form}-thickness`}>{f.thickness}</Label>
            <Input
              id={`${ids.form}-thickness`}
              type="number"
              inputMode="numeric"
              min={LIMITS.enquiryThicknessMm.min}
              max={LIMITS.enquiryThicknessMm.max}
              step={1}
              placeholder={f.thicknessPlaceholder}
              invalid={Boolean(errors.thicknessMm)}
              aria-describedby={errors.thicknessMm ? `${ids.form}-thickness-error` : undefined}
              {...register('thicknessMm', { setValueAs: toOptionalNumber })}
            />
            <FieldError id={`${ids.form}-thickness-error`} message={errors.thicknessMm?.message} />
          </div>

          <div>
            <Label htmlFor={`${ids.form}-quantity`}>{f.quantity}</Label>
            <div className="enquiry__quantity">
              <Input
                id={`${ids.form}-quantity`}
                type="number"
                inputMode="numeric"
                min={LIMITS.enquiryQuantity.min}
                max={LIMITS.enquiryQuantity.max}
                step={1}
                placeholder={f.quantityPlaceholder}
                invalid={Boolean(errors.quantity)}
                aria-describedby={errors.quantity ? `${ids.form}-quantity-error` : undefined}
                {...register('quantity', { setValueAs: toOptionalNumber })}
              />
              <Select
                value={unit}
                onValueChange={(value) => {
                  setUnit(value === 'SLAB' ? 'SLAB' : 'SQ_FT');
                }}
                aria-label={f.quantityUnit}
                options={[
                  { value: 'SQ_FT', label: f.units.SQ_FT },
                  { value: 'SLAB', label: f.units.SLAB },
                ]}
              />
            </div>
            <FieldError id={`${ids.form}-quantity-error`} message={errors.quantity?.message} />
          </div>

          <div>
            <Label htmlFor={`${ids.form}-size`}>{f.size}</Label>
            <Input
              id={`${ids.form}-size`}
              maxLength={LIMITS.enquirySize.max}
              placeholder={f.sizePlaceholder}
              invalid={Boolean(errors.size)}
              aria-describedby={errors.size ? `${ids.form}-size-error` : undefined}
              {...register('size')}
            />
            <FieldError id={`${ids.form}-size-error`} message={errors.size?.message} />
          </div>
        </div>

        {slugs.length > 0 ? (
          <div className="enquiry__wide">
            <SelectedStones slugs={slugs} onChange={setSlugs} copy={f} />
          </div>
        ) : null}

        <div className="enquiry__wide">
          <Label htmlFor={`${ids.form}-message`} required>
            {f.message}
          </Label>
          <Textarea
            id={`${ids.form}-message`}
            rows={6}
            maxLength={LIMITS.message.max}
            placeholder={f.messagePlaceholder}
            invalid={Boolean(errors.message)}
            aria-describedby={errors.message ? `${ids.form}-message-error` : undefined}
            {...register('message')}
          />
          <FieldError id={`${ids.form}-message-error`} message={errors.message?.message} />
        </div>

        <div className="enquiry__wide">
          <p className="label" id={`${ids.file}-label`}>
            {f.attachment} <span className="enquiry__optional">({f.optional})</span>
          </p>
          <div className="attach">
            <label className={buttonClasses({ variant: 'glass', size: 'sm' })} htmlFor={ids.file}>
              <Paperclip size={16} aria-hidden="true" />
              {f.attachment}
            </label>
            <input
              id={ids.file}
              className="sr-only"
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf"
              aria-describedby={`${ids.file}-help${attachmentProblem ? ` ${ids.file}-error` : ''}`}
              onChange={(event) => {
                onFile(event.target.files?.[0] ?? null);
                event.target.value = ''; // the same file can be picked again after removing it
              }}
            />
            {attachment ? (
              <span className="attach__file">
                {attachment.name}
                <button
                  type="button"
                  className="attach__remove"
                  aria-label={f.attachmentRemove}
                  onClick={() => {
                    onFile(null);
                  }}
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </span>
            ) : null}
          </div>
          <p id={`${ids.file}-help`} className="enquiry__help">
            {f.attachmentHelp}
          </p>
          <FieldError
            id={`${ids.file}-error`}
            message={
              attachmentProblem === 'tooBig'
                ? f.attachmentTooBig
                : attachmentProblem === 'type'
                  ? f.attachmentType
                  : undefined
            }
          />
        </div>

        <div className="enquiry__wide">
          <Controller
            control={control}
            name="consent"
            render={({ field }) => (
              <label className="enquiry__consent">
                <Checkbox
                  checked={field.value === true}
                  onCheckedChange={(checked) => {
                    field.onChange(checked === true);
                  }}
                  onBlur={field.onBlur}
                  aria-invalid={Boolean(errors.consent) || undefined}
                  aria-describedby={errors.consent ? `${ids.form}-consent-error` : undefined}
                />
                <span>
                  {f.consent.split('{privacy}')[0]}
                  <AppLink to="/legal-disclaimer" className="enquiry__link">
                    {f.privacy}
                  </AppLink>
                  {f.consent.split('{privacy}')[1]}
                </span>
              </label>
            )}
          />
          <FieldError id={`${ids.form}-consent-error`} message={errors.consent?.message} />
        </div>

        {/* Honeypot: invisible to people, irresistible to bots. */}
        <div className="enquiry__hp" aria-hidden="true">
          <label>
            Website
            <input type="text" name="website" tabIndex={-1} autoComplete="off" />
          </label>
        </div>

        <div className="enquiry__wide">
          <Turnstile onToken={setCaptcha} />
        </div>
      </div>

      {formError ? (
        <p id={`${ids.form}-error`} className="enquiry__error" role="alert">
          {formError}
        </p>
      ) : null}

      <div className="enquiry__actions">
        <Button type="submit" size="lg" loading={isSubmitting}>
          {isSubmitting ? f.sending : f.submit}
        </Button>
      </div>
    </form>
  );
}
